import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useState } from "react";
import type { FieldDefinition, TaskStatus } from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { can, usePermissions } from "@/auth/permissions";
import { Fab } from "@/components/shell/Fab";
import { LoadFailed, NoMatches } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { RecordDrawer } from "@/components/shell/RecordDrawer";
import { renderCell } from "@/components/table/cells";
import { Button } from "@/components/ui/button";
import { matchesFilter } from "@/lib/filter-predicate";
import { reportingNow } from "@/lib/reportClock";
import { cn } from "@/lib/utils";
import { userLabel, useUserDirectory } from "@/modules/tasks/directories";
import {
  matchesTaskSearch,
  standingProjectId,
  TASK_FETCH_LIMIT,
} from "@/modules/tasks/list-helpers";
import { isOverdue, projectTask } from "@/modules/tasks/projections";
import { SubtaskBadge } from "@/modules/tasks/components/SubtaskBadge";
import { TaskDetail } from "@/modules/tasks/screens/TaskDetail";
import { TaskForm } from "@/modules/tasks/screens/TaskForm";
import type { SectionScreenProps } from "@/modules/types";
import { useOpenRecord } from "@/routes/useOpenRecord";

const PRIORITY_FIELD: FieldDefinition = {
  name: "priority",
  label: "Priority",
  type: "enum",
  options: [
    { value: "low", label: "Low" },
    { value: "medium", label: "Medium" },
    { value: "high", label: "High" },
    { value: "urgent", label: "Urgent" },
  ],
};

const DUE_DATE_FIELD: FieldDefinition = {
  name: "dueDate",
  label: "Due date",
  type: "date",
};

/** Left to right the way work is expected to flow, not alphabetically. */
const COLUMNS: { status: TaskStatus; label: string }[] = [
  { status: "todo", label: "To do" },
  { status: "in_progress", label: "In progress" },
  { status: "blocked", label: "Blocked" },
  { status: "done", label: "Done" },
  { status: "cancelled", label: "Cancelled" },
];

/**
 * FR-TSK-14's board: the same rows, the same `Filter` and the same saved
 * views as `TaskList.tsx` — this only changes how they are arranged, not
 * where they come from, so it fetches and filters exactly the way `all`
 * does (see the comment on `taskList` for why that means client-side).
 *
 * No drag-and-drop: no DnD library is installed here, and this ticket does
 * not add one. Moving a task between columns happens in `TaskDetail`'s
 * status control instead.
 */
export function TaskBoard({
  filter,
  search,
  onClearFilter,
}: SectionScreenProps) {
  const data = useDataSource();
  const permissions = usePermissions();
  const { recordId, open } = useOpenRecord();
  const [creatingStatus, setCreatingStatus] = useState<TaskStatus | null>(null);

  const usersQuery = useUserDirectory();
  const tasksQuery = useQuery({
    queryKey: ["tasks", "list", "fetch-all"],
    queryFn: () => data.tasks.list({ limit: TASK_FETCH_LIMIT }),
  });

  if (usersQuery.isPending || tasksQuery.isPending) {
    return <TableSkeleton />;
  }

  if (usersQuery.isError) {
    return (
      <LoadFailed
        message={usersQuery.error.message}
        onRetry={() => {
          void usersQuery.refetch();
        }}
      />
    );
  }

  if (tasksQuery.isError) {
    return (
      <LoadFailed
        message={tasksQuery.error.message}
        onRetry={() => {
          void tasksQuery.refetch();
        }}
      />
    );
  }

  const users = usersQuery.data.items;
  const matching = tasksQuery.data.items.filter(
    (task) =>
      matchesTaskSearch(task, search, users) &&
      matchesFilter(projectTask(task), filter, reportingNow()),
  );
  const standingProject = standingProjectId(filter);
  const canCreate = can(permissions, "tasks.task.create");

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/*
        Hidden without `tasks.task.create` — a courtesy, not the enforcement:
        `POST /api/tasks` calls `requirePermission()` regardless.
      */}
      {canCreate ? (
        <>
          <div className="max-md:hidden flex shrink-0 justify-end border-b px-4 py-2">
            <Button
              type="button"
              size="sm"
              onClick={() => {
                setCreatingStatus("todo");
              }}
            >
              <Plus />
              New task
            </Button>
          </div>
          <Fab
            label="New task"
            icon={<Plus aria-hidden />}
            onClick={() => {
              setCreatingStatus("todo");
            }}
          />
        </>
      ) : null}

      {/*
        Keyed on the column plus the standing project, so a click on a
        different column — or a filter change while the sheet is closed —
        always mounts a fresh form seeded with that preset rather than
        reusing one whose internal state was initialised for a different
        one (`TaskForm`'s own `useState` initialiser only runs once per
        mount).
      */}
      <TaskForm
        key={`${creatingStatus ?? "closed"}:${standingProject ?? "no-project"}`}
        open={creatingStatus !== null}
        onOpenChange={(next) => {
          if (!next) setCreatingStatus(null);
        }}
        initialStatus={creatingStatus ?? "todo"}
        initialProjectId={standingProject}
        onCreated={(task) => {
          setCreatingStatus(null);
          open(task.id);
        }}
      />

      {recordId !== null ? (
        <RecordDrawer
          title={
            tasksQuery.data.items.find((task) => task.id === recordId)?.title ??
            "Task"
          }
        >
          <TaskDetail taskId={recordId} />
        </RecordDrawer>
      ) : null}

      {matching.length === 0 ? (
        <NoMatches onClear={onClearFilter} />
      ) : (
        <div className="flex min-h-0 flex-1 gap-3 overflow-x-auto p-4">
          {COLUMNS.map((column) => {
            const rows = matching.filter(
              (task) => task.status === column.status,
            );

            return (
              <div
                key={column.status}
                className="bg-muted/30 flex w-72 shrink-0 flex-col rounded-md border"
              >
                <div className="flex shrink-0 items-center justify-between border-b px-3 py-2 text-sm font-medium">
                  <span>
                    {column.label}{" "}
                    <span className="text-muted-foreground">
                      ({rows.length})
                    </span>
                  </span>
                  {canCreate ? (
                    <Button
                      type="button"
                      size="icon-sm"
                      variant="ghost"
                      aria-label={`New task in ${column.label}`}
                      onClick={() => {
                        setCreatingStatus(column.status);
                      }}
                    >
                      <Plus />
                    </Button>
                  ) : null}
                </div>

                <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-2">
                  {rows.length === 0 ? (
                    <p className="text-muted-foreground px-1 py-2 text-xs">
                      No tasks.
                    </p>
                  ) : (
                    rows.map((task) => {
                      const overdue = isOverdue(task);
                      return (
                        <button
                          key={task.id}
                          type="button"
                          onClick={() => {
                            open(task.id);
                          }}
                          className="bg-card hover:bg-accent w-full space-y-1.5 rounded-md border p-2 text-left text-sm shadow-sm"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-muted-foreground text-xs">
                              {task.reference}
                            </span>
                            {renderCell(PRIORITY_FIELD, task.priority)}
                          </div>
                          <p className="font-medium">{task.title}</p>
                          {task.parentTaskId === null ? null : (
                            <SubtaskBadge
                              task={task}
                              all={tasksQuery.data.items}
                            />
                          )}
                          <div className="text-muted-foreground flex items-center justify-between gap-2 text-xs">
                            <span>
                              {task.assigneeUserId
                                ? userLabel(users, task.assigneeUserId)
                                : "Unassigned"}
                            </span>
                            {task.dueDate ? (
                              <span
                                className={cn(
                                  overdue && "text-destructive font-medium",
                                )}
                              >
                                {renderCell(DUE_DATE_FIELD, task.dueDate)}
                              </span>
                            ) : null}
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
